import { memo, useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, TrendingUp, Users, Building, Heart } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface MySuccessionScoreOverlayProps {
  onClose: () => void;
}

export const MySuccessionScoreOverlay = memo(function MySuccessionScoreOverlay({
  onClose
}: MySuccessionScoreOverlayProps) {
  const { user } = useAuth();
  const [datapointCount, setDatapointCount] = useState<number>(0);

  useEffect(() => {
    async function loadDatapointCount() {
      if (!user?.id) return;
      try {
        const { count } = await supabase
          .from('user_mcf_values')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        setDatapointCount(count ?? 0);
      } catch (err) {
        console.error('Failed to load datapoint count:', err);
      }
    }
    loadDatapointCount();
  }, [user?.id]);
  const scores = [{
    title: "Transition Readiness",
    value: 82,
    icon: <Heart className="h-5 w-5" />,
    description: "Preparation mentally, emotionally, and financially for the exit and transition of the business, ensuring business owner's vision for the future aligns with the business's succession plan.",
    color: "text-semantic-accent"
  }, {
    title: "Strategic Planning",
    value: 90,
    icon: <TrendingUp className="h-5 w-5" />,
    description: "Development of a long-term business strategy that includes planning for the business's future direction and how it will be transitioned during the succession.",
    color: "text-chart-1"
  }, {
    title: "Business Performance",
    value: 84,
    icon: <Users className="h-5 w-5" />,
    description: "Ensuring the business is financially healthy, profitable, and attractive to potential buyers or successors, with a focus on improving performance and business valuation.",
    color: "text-semantic-success"
  }, {
    title: "Governance and Compliance",
    value: 78,
    icon: <Users className="h-5 w-5" />,
    description: "Establishing strong governance structures, legal frameworks, and ensuring enhanced compliance to facilitate a smooth ownership transfer and minimize transition risk.",
    color: "text-semantic-warning"
  }, {
    title: "Leadership and Development",
    value: 83,
    icon: <Building className="h-5 w-5" />,
    description: "Cultivating and developing future leaders to support the business to ensure that the organization and its clients have a capable leadership team ready for the post-transition.",
    color: "text-chart-3"
  }];
  return <div className="fixed inset-0 overlay-backdrop z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h1 className="text-2xl font-semibold">About mySuccession Score</h1>
            <p className="text-muted-foreground">
              Your mySuccession score evaluates {datapointCount} datapoints across the following five key dimensions:
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {scores.map((score, index) => <Card key={score.title} className="relative overflow-hidden h-fit">
                <CardHeader className="pb-3 p-4">
                  <div className="flex items-center gap-2 min-h-[24px]">
                    <div className={score.color}>
                      {score.icon}
                    </div>
                    <CardTitle className="text-base leading-tight">{score.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 p-4 pt-0">
                  <div className="flex items-baseline gap-2 min-h-[40px]">
                    <div className="text-2xl font-bold">{score.value}</div>
                  </div>
                  <Progress value={score.value} className="h-2" />
                  <CardDescription className="text-xs leading-relaxed min-h-[60px]">
                    {score.description}
                  </CardDescription>
                </CardContent>
              </Card>)}
            
            <Card className="relative overflow-hidden metric-card-primary h-fit">
              <CardHeader className="pb-3 p-4">
                <div className="flex items-center gap-2 min-h-[24px]">
                  <div className="text-semantic-primary">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-base leading-tight">Overall Assessment</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 p-4 pt-0">
                <div className="min-h-[40px] my-0"></div>
                
                <CardDescription className="text-xs leading-relaxed min-h-[60px]">
                  Your mySuccession Score of 68% indicates strong foundation with room for improvement in people connections and organizational alignment. Focus on enhancing team relationships and clarifying organizational values to maximize succession readiness.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>;
});

MySuccessionScoreOverlay.displayName = "MySuccessionScoreOverlay";

export type PillarStatus = 'implemented' | 'planned' | 'future';